import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useWallet } from "../lib/wallet";
import { useAlerts } from "../lib/queries";
import { api } from "../lib/api";
import { fmtPrice } from "../lib/format";
import { useShare } from "../components/terminal/ShareProvider";

const supported = typeof window !== "undefined" && "Notification" in window;

// Watches the connected wallet's deals (polled every 4s via /api/alerts) and fires
// a browser Notification + in-app toast the moment the OTHER side funds, a deal
// settles, or the counterparty posts a new counter-offer. Snapshot is seeded on
// connect so nothing fires for pre-existing state, and never for the wallet's own actions.
export function useCounterpartyAlerts() {
  const w = useWallet();
  const { data: rows } = useAlerts(w.address);
  const prev = useRef(new Map());
  const seeded = useRef(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "unsupported");
  const openShare = useShare();

  const requestPermission = useCallback(async () => {
    if (!supported) return;
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") toast.success("Counterparty alerts enabled", { description: "You'll be pinged when the other side funds, counters, or a deal settles." });
    else if (p === "denied") toast.error("Alerts blocked", { description: "Enable notifications for this site in your browser settings." });
  }, []);

  const fire = useCallback((title, body, action) => {
    toast(title, { description: body, ...(action ? { action } : {}) });
    if (supported && Notification.permission === "granted") {
      try { new Notification(title, { body, tag: title }); } catch (e) { console.debug("Notification display failed:", e); }
    }
  }, []);

  useEffect(() => { seeded.current = false; prev.current = new Map(); }, [w.address]);

  useEffect(() => {
    if (!w.connected || !rows) return;
    const me = w.address;
    const next = new Map();
    for (const r of rows) next.set(r.id, { status: r.status, seller_funded: r.seller_funded, buyer_funded: r.buyer_funded, counterId: r.latest_counter?.id || null });

    if (!seeded.current) { prev.current = next; seeded.current = true; return; }

    for (const r of rows) {
      const before = prev.current.get(r.id);
      if (!before) continue;
      const isSeller = r.seller_address === me;
      const isBuyer = r.buyer_address === me;

      if (before.status !== "settled" && r.status === "settled") {
        fire(`Deal ${r.id} settled`, `${r.token_symbol}/${r.stock} released automatically — both sides funded.`,
          { label: "Share", onClick: () => api.deal(r.id).then(openShare).catch((e) => console.debug("share fetch failed:", e)) });
        continue;
      }
      if (isSeller && !before.buyer_funded && r.buyer_funded) {
        fire(`Buyer funded ${r.id}`, `The buyer deposited on your ${r.token_symbol}/${r.stock} deal. It releases as soon as you fund.`);
      } else if (isBuyer && !before.seller_funded && r.seller_funded) {
        fire(`Seller funded ${r.id}`, `The seller deposited tokens on your ${r.token_symbol}/${r.stock} deal. It releases as soon as you fund.`);
      }

      const lc = r.latest_counter;
      if (lc && lc.id !== before.counterId && lc.sender !== me && !lc.accepted) {
        fire(`New counter on ${r.id}`, `Counterparty countered at ${fmtPrice(lc.price_usd)}/M on ${r.token_symbol}/${r.stock}. Open the deal's chat to accept.`);
      }
    }
    prev.current = next;
  }, [rows, w.connected, w.address, fire, openShare]);

  return { supported, permission, enabled: permission === "granted", requestPermission };
}
